import { useEffect, type ReactNode } from "react";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useStateMachine } from "@/core/arch/state_machine";
import { Button } from "@/components/ui/base/button";
import {
  AssistantGapsBloc,
  WINDOW_OPTIONS,
  type WindowDays,
} from "@/application/assistant/assistant_gaps_bloc";
import { DataAssistantRepository } from "@/application/assistant/assistant_repository";
import {
  GAP_DISPOSITIONS,
  gapDispositionCopy,
  RETENTION_DAYS,
  type QuestionGroup,
} from "@/application/assistant/types";
import { formatCount, formatDateTime } from "../signals/format";

/**
 * What merchants asked the assistant that it did not answer.
 *
 * Every turn records a disposition, and a stale FAQ does not make the
 * assistant go quiet — it makes it improvise, so these rows are the only
 * reliable way a gap gets noticed.
 *
 * `out_of_scope` is two populations, and this page used to call both of them
 * missing documentation. "Unanswered" is the model finding nothing in the
 * reference that bore on the question. "Lost to a failure" is the assistant
 * having an answer and failing to deliver it, which no FAQ fixes — those
 * carry the reason, because `unresolved_ref` and a model outage are different
 * problems with different owners.
 *
 * Questions are real merchant text. The page is CPOS-Admin only, enforced by
 * the model's authorization, and carries no export.
 */
export function AssistantGapsView() {
  const [state, bloc] = useStateMachine(
    () => new AssistantGapsBloc(new DataAssistantRepository()),
  );

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  const copy = gapDispositionCopy(state.disposition);
  const loading = state.status === "loading";

  return (
    <div>
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground text-2xl font-bold">Assistant gaps</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Questions the assistant handed off instead of answering, grouped by
            question and ranked by how many merchants asked. Questions it knew
            and lost are listed separately.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => void bloc.load()}
        >
          <RefreshCw
            className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </header>

      <RetentionNotice />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {GAP_DISPOSITIONS.map((entry) => (
          <FilterChip
            key={entry.id}
            active={state.disposition === entry.id}
            disabled={loading}
            onClick={() => void bloc.setDisposition(entry.id)}
          >
            {entry.label}
          </FilterChip>
        ))}
        <span className="text-subtle-foreground mx-1 text-xs">·</span>
        {WINDOW_OPTIONS.map((days) => (
          <FilterChip
            key={days}
            active={state.windowDays === days}
            disabled={loading}
            onClick={() => void bloc.setWindow(days as WindowDays)}
          >
            {`Last ${days}d`}
          </FilterChip>
        ))}
      </div>

      <p className="text-muted-foreground mb-4 text-sm">{copy.hint}</p>

      {loading && (
        <div className="bg-card text-muted-foreground flex items-center justify-center gap-2 rounded-lg border py-16 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Reading the last {state.windowDays} days…
        </div>
      )}

      {state.status === "error" && (
        <div className="border-destructive/50 bg-destructive/10 flex items-start gap-3 rounded-lg border px-4 py-4">
          <AlertCircle className="text-destructive mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-destructive text-sm font-medium">
              Could not read assistant turns
            </p>
            <p className="text-destructive mt-0.5 text-sm">{state.error}</p>
          </div>
        </div>
      )}

      {state.status === "loaded" && (
        <>
          {state.truncated && (
            <p className="border-warning/50 bg-warning/10 text-warning mb-3 rounded-md border px-3 py-2 text-xs">
              This window holds more turns than one read returns. The counts
              below cover only the most recent {formatCount(state.fetched)}{" "}
              turns read and are a floor, not a total — narrow the window for an
              exact count.
            </p>
          )}
          <GroupTable
            groups={state.groups}
            turns={state.turns}
            label={copy.label}
          />
        </>
      )}
    </div>
  );
}

/**
 * States the retention window on the page.
 *
 * Load-bearing: without it an empty list reads as "no gaps" when it may mean
 * the rows expired, and that is the opposite conclusion.
 *
 * "Roughly", because deletion runs on a TTL sweep rather than a clock. Stating
 * an exact day would promise a stronger retention guarantee than the table
 * gives, on a page whose rows are merchants' own words.
 */
function RetentionNotice() {
  return (
    <p className="bg-muted/50 text-muted-foreground mb-4 rounded-md border px-3 py-2 text-xs">
      This page covers roughly the last {RETENTION_DAYS} days; turns are deleted
      after that, shortly rather than exactly on the day. An empty list means no
      one asked within the window, not that nothing was ever asked.
    </p>
  );
}

function FilterChip({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      // Selection is otherwise only a colour change, which a screen reader
      // cannot report. Without this the active disposition and window are
      // invisible to anyone not looking at the chips.
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
        active
          ? "bg-foreground text-background border-transparent"
          : "bg-card text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function GroupTable({
  groups,
  turns,
  label,
}: {
  groups: QuestionGroup[];
  turns: number;
  label: string;
}) {
  if (groups.length === 0) {
    return (
      <div className="bg-card rounded-lg border px-6 py-16 text-center">
        <p className="text-foreground text-sm">Nothing in this window</p>
        <p className="text-muted-foreground mt-1 text-sm">
          No turn in the selected window took this exit.
        </p>
      </div>
    );
  }

  return (
    <section className="bg-card overflow-hidden rounded-lg border">
      <header className="flex items-baseline justify-between gap-3 border-b px-4 py-3">
        <h2 className="text-foreground text-sm font-semibold">
          {label} · {formatCount(groups.length)} distinct{" "}
          {groups.length === 1 ? "question" : "questions"}
        </h2>
        <p className="text-subtle-foreground text-xs">
          {formatCount(turns)} {turns === 1 ? "turn" : "turns"}
        </p>
      </header>
      <ul className="divide-y">
        {groups.map((group) => (
          <li key={group.key} className="flex items-start gap-4 px-4 py-3">
            <div className="w-20 shrink-0">
              <p className="text-foreground text-lg font-semibold tabular-nums">
                {formatCount(group.merchants)}
              </p>
              <p className="text-subtle-foreground text-xs">
                {group.merchants === 1 ? "merchant" : "merchants"}
                {group.occurrences > group.merchants &&
                  ` · ${formatCount(group.occurrences)} asks`}
              </p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground text-sm break-words">
                {group.question}
              </p>
              <p className="text-subtle-foreground mt-0.5 text-xs">
                Last asked {formatDateTime(group.lastAsked)}
              </p>
              {group.failureReasons.length > 0 && (
                <p className="text-subtle-foreground mt-1 flex flex-wrap gap-1.5 text-xs">
                  {group.failureReasons.map((reason) => (
                    <span
                      key={reason}
                      className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 font-mono"
                    >
                      {reason}
                    </span>
                  ))}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
